import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import PageSEO from "../components/SEO";
import CatalogHiddenBanner from "../components/CatalogHiddenBanner";
import { Button } from "../components/ui/button";
import { useShop, useShopHref } from "../lib/shop-context";

const PAGE_SIZE = 24;

export default function CategoryPage() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const sub = searchParams.get("sub") || "";
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
  const shopCtx = useShop();
  const shop = shopCtx?.shop || "envio";
  const shopHref = useShopHref();

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories", { params: { shop } }).then((r) => setCategories(r.data)).catch(() => {});
  }, [shop]);

  const current = useMemo(
    () => categories.find((c) => c.slug === slug),
    [categories, slug]
  );

  useEffect(() => {
    setLoading(true);
    const params = { category: slug, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE, shop };
    if (sub) params.subcategory = sub;
    api
      .get("/products", { params })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [slug, sub, page, shop]);

  const setSub = (newSub) => {
    const next = new URLSearchParams(searchParams);
    if (newSub) next.set("sub", newSub);
    else next.delete("sub");
    next.delete("page");
    setSearchParams(next);
  };

  const changePage = (p) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(p));
    setSearchParams(next);
  };

  return (
    <div data-testid="category-page" className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-10">
      {/* Fase 3 · Catalog kill-switch. */}
      {shopCtx?.catalog_hidden ? (
        <CatalogHiddenBanner />
      ) : (
        <>
      <PageSEO
        title={current?.name ? `${current.name} halal desde España` : slug}
        description={
          current?.name
            ? `Compra ${current.name.toLowerCase()} online en MercaGo y envía a los campamentos saharauis. Productos halal seleccionados, entrega directa a tu familia en Tindouf.`
            : undefined
        }
        canonical={`https://mercago.one/envio/categoria/${slug}${sub ? `?sub=${sub}` : ""}`}
        jsonLd={current?.name ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://mercago.one/envio/" },
            { "@type": "ListItem", "position": 2, "name": current.name, "item": `https://mercago.one/envio/categoria/${slug}` },
            ...(sub ? [{ "@type": "ListItem", "position": 3, "name": current.children?.find((s) => s.slug === sub)?.name || sub, "item": `https://mercago.one/envio/categoria/${slug}?sub=${sub}` }] : []),
          ],
        } : null}
      />
      {/* Breadcrumb */}
      <nav
        data-testid="breadcrumb"
        className="text-sm text-neutral-500 mb-6 flex items-center gap-2"
      >
        <Link to={shopHref("/")} className="hover:underline">
          Inicio
        </Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-neutral-900 font-medium">
          {current?.name || slug}
        </span>
        {sub && current && (
          <>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-neutral-900 font-medium">
              {current.children?.find((s) => s.slug === sub)?.name || sub}
            </span>
          </>
        )}
      </nav>

      <div className="grid lg:grid-cols-[240px_1fr] gap-8">
        {/* Sidebar */}
        <aside
          data-testid="subcategory-sidebar"
          className="hidden lg:block sticky top-24 self-start"
        >
          <div className="text-xs uppercase tracking-wider text-neutral-500 mb-3">
            Subcategorías
          </div>
          <ul className="flex flex-col gap-1">
            <li>
              <button
                data-testid="subcat-btn-all"
                onClick={() => setSub("")}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm ${
                  !sub
                    ? "bg-[#F1FAF3] text-[#00953B] font-semibold"
                    : "hover:bg-neutral-50 text-neutral-700"
                }`}
              >
                Todo
              </button>
            </li>
            {(current?.children || []).map((s) => (
              <li key={s.id}>
                <button
                  data-testid={`subcat-btn-${s.slug}`}
                  onClick={() => setSub(s.slug)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm ${
                    sub === s.slug
                      ? "bg-[#F1FAF3] text-[#00953B] font-semibold"
                      : "hover:bg-neutral-50 text-neutral-700"
                  }`}
                >
                  {s.name}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Product grid */}
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">
            {current?.name || "Categoría"}
          </h1>
          {sub && current && (
            <p className="text-neutral-600 mb-6" data-testid="subcategory-title">
              {current.children?.find((s) => s.slug === sub)?.name}
            </p>
          )}

          {/* Mobile subcat chips */}
          <div className="lg:hidden flex gap-2 overflow-x-auto pb-3 mb-4 -mx-1 px-1">
            <button
              onClick={() => setSub("")}
              className={`shrink-0 px-4 py-1.5 rounded-full text-sm border ${
                !sub
                  ? "bg-[#00953B] text-white border-[#00953B]"
                  : "bg-white text-neutral-700 border-neutral-200"
              }`}
            >
              Todo
            </button>
            {(current?.children || []).map((s) => (
              <button
                key={s.id}
                onClick={() => setSub(s.slug)}
                className={`shrink-0 px-4 py-1.5 rounded-full text-sm border ${
                  sub === s.slug
                    ? "bg-[#00953B] text-white border-[#00953B]"
                    : "bg-white text-neutral-700 border-neutral-200"
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="aspect-[3/4] rounded-xl bg-neutral-100 animate-pulse"
                />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div
              data-testid="category-empty-state"
              className="rounded-2xl border border-dashed border-neutral-300 p-12 text-center"
            >
              <p className="text-lg font-semibold">No hay productos en esta categoría</p>
              <p className="text-sm text-neutral-500 mt-1">
                Prueba con otra subcategoría o vuelve al inicio.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {products.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
              {/* Pagination (simple prev/next since count endpoint absent) */}
              {(page > 1 || products.length === PAGE_SIZE) && (
                <div
                  data-testid="pagination"
                  className="mt-8 flex items-center justify-center gap-3"
                >
                  <Button
                    data-testid="page-prev-btn"
                    variant="outline"
                    disabled={page <= 1}
                    onClick={() => changePage(page - 1)}
                    className="rounded-full border-neutral-200"
                  >
                    Anterior
                  </Button>
                  <span className="text-sm text-neutral-500">Página {page}</span>
                  <Button
                    data-testid="page-next-btn"
                    variant="outline"
                    disabled={products.length < PAGE_SIZE}
                    onClick={() => changePage(page + 1)}
                    className="rounded-full border-neutral-200"
                  >
                    Siguiente
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
        </>
      )}
    </div>
  );
}
